import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Sparkles, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type { SubCase } from '@/lib/sub-cases/types';
import type { HotelOption } from '@/lib/allocation/types';
import { hotelGuests } from '@/lib/allocation/hotel-guests';
import type { Reservation } from '@/lib/booking/types';
import { GroupCard } from '@/components/allocation/group-card';
import { HotelDropCard } from '@/components/allocation/hotel-drop-card';
import { ReservationRow } from '@/components/allocation/reservation-row';
import { useNow } from '@/components/allocation/use-now';
import type { GroupAssignment } from '@/components/allocation/types';
import { useSearchHotels } from './use-allocation';
import { useCancelReservation, useReservations } from './use-reservations';
import { useGroupLocks } from './use-group-locks';
import { type usePendingHolds } from './use-holds';
import { estimateRooms, pnrLabel } from './group-utils';
import { DEFAULT_STAY_PLAN, type StayPlanType } from './stay-plan';
import { newIdempotencyKey } from '@/lib/uuid';

// Passenger→hotel assignment board (mvp AccommodationTab parity). Left: PNR
// group cards with a stay-plan selector (only `hotel` groups need rooms),
// draggable onto hotels. Right: policy-ranked hotel offers as drop zones
// showing ★/price/distance/capacity + the assigned groups, each with the
// HOLD → Confirm → BOOKED lifecycle + Cancel saga inline.
//
// Real-data adaptation: the booking domain has hotel *offers* (capacity N rooms
// at a rate), not discrete room numbers, so a hotel card is the drop zone and a
// group consumes `estimateRooms` of its capacity. Dropping a group places a
// soft-hold; confirming books it. Stay plans + the group→hotel assignment map
// are operator session state (no backend field yet).

type HoldsApi = ReturnType<typeof usePendingHolds>;

interface AssignmentBoardProps {
  caseUrn: string;
  subCases: SubCase[];
  airportCode: string;
  holds: HoldsApi;
}

export function AssignmentBoard({ caseUrn, subCases, airportCode, holds }: AssignmentBoardProps) {
  const { t } = useTranslation('caseWork');
  const reservationsQ = useReservations(caseUrn);
  const cancel = useCancelReservation(caseUrn);
  const reservations = useMemo(
    () => (Array.isArray(reservationsQ.data) ? reservationsQ.data : []),
    [reservationsQ.data],
  );

  const [stayPlans, setStayPlans] = useState<Record<string, StayPlanType>>({});
  const [dragUrn, setDragUrn] = useState<string | null>(null);
  const [dragOverHotel, setDragOverHotel] = useState<string | null>(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  // Tick only while a hold actually carries a server deadline to count down —
  // a hold the server granted no clock for renders a static "Hold active" and
  // needs no ticker. Render tick, not polling.
  const now = useNow(holds.holds.some((h) => !!h.expiresAt));

  const planFor = (urn: string): StayPlanType => stayPlans[urn] ?? DEFAULT_STAY_PLAN;

  // Reservation status by URN, for resolving confirmed-assignment state.
  const resByUrn = useMemo(() => {
    const m = new Map<string, Reservation>();
    for (const r of reservations) m.set(r.reservationUrn, r);
    return m;
  }, [reservations]);

  // Drop confirmed records whose reservation has been cancelled/failed so the
  // group returns to the unassigned pool.
  useEffect(() => {
    for (const [urn, c] of Object.entries(holds.confirmed)) {
      const r = resByUrn.get(c.reservationUrn);
      if (r && (r.status === 'CANCELLED' || r.status === 'FAILED')) holds.clearConfirmed(urn);
    }
  }, [resByUrn, holds]);

  // group URN → its current assignment (held or booked).
  const assignmentByGroup = useMemo(() => {
    const m = new Map<string, GroupAssignment>();
    for (const h of holds.holds) {
      m.set(h.subCaseUrn, {
        kind: 'held',
        hotelUrn: h.offer.hotelUrn,
        hotelName: h.offer.name,
        hold: h,
      });
    }
    for (const c of Object.values(holds.confirmed)) {
      const r = resByUrn.get(c.reservationUrn);
      if (r && (r.status === 'CANCELLED' || r.status === 'FAILED')) continue;
      // A live hold takes precedence (re-hold after a failed confirm).
      if (m.has(c.subCaseUrn)) continue;
      m.set(c.subCaseUrn, {
        kind: 'booked',
        hotelUrn: c.hotelUrn,
        hotelName: c.hotelName,
        reservationUrn: c.reservationUrn,
        status: r?.status ?? 'PENDING',
      });
    }
    return m;
  }, [holds.holds, holds.confirmed, resByUrn]);

  const hotelGroups = useMemo(
    () => subCases.filter((sc) => (stayPlans[sc.urn] ?? DEFAULT_STAY_PLAN) === 'hotel'),
    [subCases, stayPlans],
  );
  const unassignedGroups = hotelGroups.filter((sc) => !assignmentByGroup.has(sc.urn));
  const assignedCount = hotelGroups.length - unassignedGroups.length;

  // Search — guests sized to the largest hotel group so offer capacity is realistic.
  const maxGuests = Math.max(1, ...hotelGroups.map((sc) => hotelGuests(sc.passengers)));
  const search = useSearchHotels(caseUrn, airportCode, maxGuests);
  const offers = useMemo(() => search.data ?? [], [search.data]);
  const hasSearched = search.isPending || search.isSuccess || search.isError;

  const groupByUrn = useMemo(() => new Map(subCases.map((sc) => [sc.urn, sc])), [subCases]);

  // Per-group lock state — writes for a group are enabled only if the operator
  // holds THAT group's lock (product-owner decision; enforced server-side too).
  const subCaseUrns = useMemo(() => subCases.map((sc) => sc.urn), [subCases]);
  const lockFor = useGroupLocks(subCaseUrns);

  // Remaining capacity per offer = availableRooms − rooms already assigned there.
  const assignedRoomsByHotel = useMemo(() => {
    const m = new Map<string, number>();
    for (const [urn, a] of assignmentByGroup) {
      const group = groupByUrn.get(urn);
      const rooms = a.kind === 'held' ? a.hold.roomCount : group ? estimateRooms(group) : 1;
      m.set(a.hotelUrn, (m.get(a.hotelUrn) ?? 0) + rooms);
    }
    return m;
  }, [assignmentByGroup, groupByUrn]);

  const dropOnHotel = async (offer: HotelOption, subCaseUrn: string): Promise<void> => {
    const group = groupByUrn.get(subCaseUrn);
    if (!group || assignmentByGroup.has(subCaseUrn) || planFor(subCaseUrn) !== 'hotel') return;
    // Gate on the group's lock — only the holder can place a hold for it.
    if (!lockFor(subCaseUrn).isMine) return;
    const rooms = estimateRooms(group);
    const remaining = offer.availableRooms - (assignedRoomsByHotel.get(offer.hotelUrn) ?? 0);
    if (remaining < rooms) return;
    await holds.placeHold(offer, subCaseUrn, rooms);
  };

  // Groups the operator can act on right now: unassigned, hotel-plan, AND locked
  // by me. Auto-assign only touches these.
  const assignableGroups = unassignedGroups.filter((sc) => lockFor(sc.urn).isMine);

  const autoAssign = async (): Promise<void> => {
    setAutoAssigning(true);
    try {
      let available = offers;
      if (!hasSearched) {
        available = await search.mutateAsync();
      }
      // Priority: families (children/infants) first, then larger groups. Only
      // groups the operator holds the lock on are assignable.
      const sorted = [...assignableGroups].sort((a, b) => {
        const fam = (sc: SubCase) => (sc.passengers.some((p) => p.paxType !== 'ADT') ? 0 : 1);
        return fam(a) - fam(b) || b.passengers.length - a.passengers.length;
      });
      const usedRooms = new Map(assignedRoomsByHotel);
      for (const group of sorted) {
        const rooms = estimateRooms(group);
        // Prefer cheapest-compliant (offers arrive pre-sorted) AND consolidation:
        // a hotel that already has assignments wins ties.
        const candidates = available
          .filter(
            (o) =>
              o.policyCompliant && o.availableRooms - (usedRooms.get(o.hotelUrn) ?? 0) >= rooms,
          )
          .sort((a, b) => {
            const used = (o: HotelOption) => (usedRooms.get(o.hotelUrn) ? 0 : 1);
            // Cheapest PRICE first; an unpriced offer (no price card) sorts last.
            return used(a) - used(b) || (a.ratePerRoomUSD ?? Infinity) - (b.ratePerRoomUSD ?? Infinity);
          });
        const pick = candidates[0];
        if (!pick) continue;
        await holds.placeHold(pick, group.urn, rooms);
        usedRooms.set(pick.hotelUrn, (usedRooms.get(pick.hotelUrn) ?? 0) + rooms);
      }
    } finally {
      setAutoAssigning(false);
    }
  };

  // Reservations whose hotel isn't in the current offer list — still surface them.
  const offerHotelUrns = new Set(offers.map((o) => o.hotelUrn));
  const orphanReservations = reservations.filter((r) => !offerHotelUrns.has(r.hotelUrn));

  return (
    // Bounded board (§18.1): on desktop the two panes sit side by side and each
    // scrolls its own list independently; below lg they stack and the board is
    // the single scroll region. min-h-0 lets the inner lists actually shrink.
    <div
      className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto lg:flex-row lg:overflow-hidden"
      data-testid="assignment-board"
    >
      {/* Left — passenger group pool (header fixed, list scrolls) */}
      <Card className="flex min-h-0 flex-col lg:w-[34%]">
        <CardHeader className="shrink-0 pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" aria-hidden /> {t('assignment.groupsTitle')}
            </CardTitle>
            <Badge
              variant={
                assignedCount === hotelGroups.length && hotelGroups.length > 0 ? 'success' : 'muted'
              }
              size="sm"
            >
              {t('assignment.assignedCount', {
                assigned: assignedCount,
                total: hotelGroups.length,
              })}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">{t('assignment.dragHint')}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 w-full"
            disabled={autoAssigning || assignableGroups.length === 0}
            title={
              assignableGroups.length === 0
                ? t('assignment.autoAssignDisabledHint')
                : undefined
            }
            onClick={() => void autoAssign()}
          >
            <Sparkles
              className={cn('mr-1 h-3 w-3', autoAssigning && 'animate-pulse')}
              aria-hidden
            />
            {autoAssigning
              ? t('assignment.assigning')
              : t('assignment.autoAssign', { n: assignableGroups.length })}
          </Button>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 space-y-2 overflow-y-auto" data-testid="group-list">
          {subCases.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('assignment.noGroups')}
            </p>
          ) : (
            subCases.map((sc) => (
              <GroupCard
                key={sc.urn}
                group={sc}
                plan={planFor(sc.urn)}
                lock={lockFor(sc.urn)}
                onPlanChange={(plan) => setStayPlans((prev) => ({ ...prev, [sc.urn]: plan }))}
                assignment={assignmentByGroup.get(sc.urn)}
                dragging={dragUrn === sc.urn}
                onDragStart={() => setDragUrn(sc.urn)}
                onDragEnd={() => setDragUrn(null)}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Right — hotels (policy-ranked) as drop zones. Header fixed, list scrolls
          (on lg). Below lg the board itself is the scroll region. */}
      <div className="flex min-h-0 flex-col gap-3 lg:flex-1">
        <div className="flex shrink-0 items-center justify-between">
          <p className="text-sm font-medium">
            {t('assignment.hotelsTitle', { airport: airportCode })}{' '}
            <span className="font-normal text-muted-foreground">
              {t('assignment.cheapestFirst')}
            </span>
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={search.isPending}
            onClick={() => search.mutate()}
          >
            {hasSearched ? (
              <RefreshCw
                className={cn('mr-1 h-3 w-3', search.isPending && 'animate-spin')}
                aria-hidden
              />
            ) : (
              <Search className="mr-1 h-3 w-3" aria-hidden />
            )}
            {hasSearched ? t('assignment.reSearch') : t('assignment.searchHotels')}
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 lg:overflow-y-auto" data-testid="hotel-list">
          {!hasSearched ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {t('assignment.searchPrompt')}
              </CardContent>
            </Card>
          ) : search.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : search.isError ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-destructive">
                {t('assignment.searchFailed')}
              </CardContent>
            </Card>
          ) : offers.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                {t('assignment.noCompliantHotels')}
              </CardContent>
            </Card>
          ) : (
            offers.map((offer) => (
              <HotelDropCard
                key={offer.offerUrn}
                offer={offer}
                assignedRooms={assignedRoomsByHotel.get(offer.hotelUrn) ?? 0}
                holdsHere={holds.holds.filter((h) => h.offer.hotelUrn === offer.hotelUrn)}
                reservationsHere={reservations.filter((r) => r.hotelUrn === offer.hotelUrn)}
                dragActive={
                  dragUrn !== null &&
                  !assignmentByGroup.has(dragUrn) &&
                  planFor(dragUrn) === 'hotel' &&
                  lockFor(dragUrn).isMine
                }
                isOver={dragOverHotel === offer.hotelUrn}
                now={now}
                labelForSubCase={(urn) => pnrLabel(groupByUrn.get(urn)?.pnrUrn ?? urn)}
                lockForSubCase={lockFor}
                cancelPending={cancel.isPending}
                onDragOver={() => setDragOverHotel(offer.hotelUrn)}
                onDragLeave={() => setDragOverHotel((cur) => (cur === offer.hotelUrn ? null : cur))}
                onDrop={() => {
                  const urn = dragUrn;
                  setDragOverHotel(null);
                  setDragUrn(null);
                  if (urn) void dropOnHotel(offer, urn);
                }}
                onConfirm={holds.confirm}
                onRelease={holds.release}
                onRehold={holds.rehold}
                onDismiss={holds.dismiss}
                onCancel={(reservationUrn) =>
                  cancel.mutate({
                    reservationUrn,
                    reasonCode: 'OPERATOR_CANCEL',
                    idempotencyKey: newIdempotencyKey(),
                  })
                }
              />
            ))
          )}

          {orphanReservations.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('assignment.otherBookedRooms')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {orphanReservations.map((r) => (
                  <ReservationRow
                    key={r.reservationUrn}
                    reservation={r}
                    cancelPending={cancel.isPending}
                    onCancel={() =>
                      cancel.mutate({
                        reservationUrn: r.reservationUrn,
                        reasonCode: 'OPERATOR_CANCEL',
                        idempotencyKey: newIdempotencyKey(),
                      })
                    }
                  />
                ))}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
